import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import cogoToast from 'cogo-toast';
import Modal from '../General/Modal';
import { setInvoiceExemption } from '../../api/spotlights';
import './SpotlightInvoiceExemption.scss';

const SpotlightInvoiceExemption = ({ brand, onBrandPatch, onRefresh }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button className='sl-btn secondary small' onClick={() => setOpen(true)}>
        {brand.invoiceExempt ? 'Resume invoicing' : 'Disable new invoices'}
      </button>
      {open && <InvoiceExemptionForm brand={brand} onBrandPatch={onBrandPatch} onRefresh={onRefresh} onClose={() => setOpen(false)} />}
    </>
  );
};

const InvoiceExemptionForm = ({ brand, onBrandPatch, onRefresh, onClose }) => {
  const [reason, setReason] = useState('');
  const [startDate, setStartDate] = useState('');
  const [financeConfirmed, setFinanceConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const exempt = !!brand.invoiceExempt;
  const title = exempt ? 'Resume customer invoicing' : 'Disable new customer invoices';
  const tomorrow = moment
    .utc()
    .add(1, 'day')
    .format('YYYY-MM-DD');
  const minimumDate = brand.invoiceStartDate > tomorrow ? brand.invoiceStartDate : tomorrow;
  const date = moment.utc(startDate, 'YYYY-MM-DD', true);
  const dateValid = date.isValid() && startDate >= minimumDate && (brand.billingCadence === 'weekly' ? date.isoWeekday() === 1 : date.date() === 1);
  const periodLabel = brand.billingCadence === 'weekly' ? 'Monday' : 'first day of a month';
  const canSave = !saving && reason.trim() && (!exempt || (dateValid && financeConfirmed));

  const save = async e => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await setInvoiceExemption(brand.Brand_id, {
        exempt: !exempt,
        reason,
        startDate: exempt ? startDate : null,
        financeConfirmed,
        expectedExempt: exempt,
        expectedStartDate: brand.invoiceStartDate || null
      });
      onBrandPatch(brand.Brand_id, {
        invoiceExempt: updated.invoiceExempt,
        invoiceStartDate: updated.invoiceStartDate,
        invoiceEligibilityReason: updated.invoiceEligibilityReason
      });
      cogoToast.success('Customer invoice eligibility updated.');
      onClose();
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Could not update invoice eligibility. Please try again.');
      setSaving(false);
    }
  };

  return createPortal(
    <div className='spotlights-admin-outer sl-invoice-exemption-portal'>
      <Modal close={() => !saving && onClose()} container_class='sl-modal sl-invoice-exemption-modal' focusTrap labelledBy='invoice-exemption-title'>
        <div className='sl-modal-head'>
          <h2 id='invoice-exemption-title'>{title}</h2>
        </div>
        <form className='sl-modal-body' onSubmit={save}>
          <p className='sl-exemption-brand'>{brand.brandName}</p>
          <p className='sl-exemption-description'>
            {exempt
              ? 'New invoices will include spend from the date you choose. All earlier unbilled spend stays excluded.'
              : 'No new customer ad-spend invoices or platform fees will be generated. Ads and spend reporting continue.'}
          </p>
          <p className='sl-exemption-note'>Existing invoices, Stripe collections and subscription charges stay unchanged.</p>
          {exempt && (
            <>
              <label className='sl-exemption-field'>
                Bill spend from (UTC)
                <input type='date' required min={minimumDate} value={startDate} onChange={e => setStartDate(e.target.value)} disabled={saving} />
                <span>Choose a future {periodLabel}. Previously excluded spend cannot be billed.</span>
              </label>
              {startDate && !dateValid && (
                <p className='sl-exemption-error' role='alert'>
                  Choose a {periodLabel} on or after {minimumDate}.
                </p>
              )}
              <label className='sl-exemption-confirmation'>
                <input type='checkbox' required checked={financeConfirmed} disabled={saving} onChange={e => setFinanceConfirmed(e.target.checked)} />I
                confirmed this restart date and the exclusion of earlier spend with Finance.
              </label>
            </>
          )}
          <label className='sl-exemption-field'>
            Reason for changing invoice eligibility
            <input type='text' required maxLength={500} value={reason} disabled={saving} onChange={e => setReason(e.target.value)} />
          </label>
          {error && (
            <div className='sl-exemption-error' role='alert'>
              {error}
              {onRefresh && (
                <button
                  type='button'
                  className='sl-btn secondary small'
                  onClick={() => {
                    onClose();
                    onRefresh();
                  }}
                >
                  Refresh billing
                </button>
              )}
            </div>
          )}
          <div className='sl-exemption-actions'>
            <button type='button' className='sl-btn secondary' disabled={saving} onClick={onClose}>
              Cancel
            </button>
            <button className='sl-btn' disabled={!canSave}>
              {saving ? 'Saving…' : exempt ? 'Resume invoicing' : 'Disable new invoices'}
            </button>
          </div>
        </form>
      </Modal>
    </div>,
    document.body
  );
};

export default SpotlightInvoiceExemption;
